import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useCompanySettings } from '../hooks/useCompanySettings';
import ScrollToTop from '../components/ScrollToTop';
import PreLoader from '../components/PreLoader';
import SEO from '../components/SEO';
import BreadcrumbNav from '../components/seo/BreadcrumbNav';
import { BlogPostSkeleton } from '../components/BlogSkeletons';
import {
  resolveOfficialBaseUrl,
  toSchemaIsoDate,
  buildBlogPostBreadcrumbs,
  buildBlogPostBreadcrumbSchema,
  buildBlogPostingJsonLd,
  buildBlogFaqSchema,
  resolveBlogSeoMeta,
  buildPropertyPath,
} from '../utils/seo';
import { fetchHomeFeaturedProperties } from '../services/propertiesCatalog';
import {
  fetchPublishedBlogPosts,
  extractBlogCategoryCounts,
} from '../services/blogCatalog';

function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPreLoading, setIsPreLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [hasViewed, setHasViewed] = useState(false);

  // Hook para configurações da empresa
  const companySettings = useCompanySettings();
  const officialBaseUrl = resolveOfficialBaseUrl(companySettings);

  // Estados para animações
  const [, setIsLoaded] = useState(false);
  const [visibleElements, setVisibleElements] = useState({
    header: false,
    content: false,
    sidebar: false
  });

  // Refs para os elementos que serão observados
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);

  const handlePostView = useCallback(async (postData) => {
    if (!postData) return;

    const viewKey = `blog_view_${postData.id}`;
    const lastView = localStorage.getItem(viewKey);
    const now = new Date().getTime();

    if (lastView) {
      const timeDiff = now - parseInt(lastView);
      const hoursDiff = timeDiff / (1000 * 60 * 60);
      
      if (hoursDiff < 12) {
        setHasViewed(true);
        return;
      }
    }

    try {
      const db = getFirestore();
      const postRef = doc(db, 'blogPosts', postData.id);
      
      await updateDoc(postRef, {
        views: increment(1)
      });

      localStorage.setItem(viewKey, now.toString());
      setHasViewed(true);

    } catch (error) {
      console.error('Erro ao registrar visualização:', error);
    }
  }, []);

  // Ao entrar no post (ou trocar de slug), sempre começa no topo
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [id]);

  useEffect(() => {
    if (loading) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [loading, id]);

  // Carrega o post primeiro (caminho crítico); sidebar depois
  useEffect(() => {
    let cancelled = false;

    const loadPost = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setPost(null);
        const db = getFirestore();

        // 1) Lista publicada (rules-safe: status==published) e resolve por slug/id
        //    Evita query composta slug+status (índice) e a query só por slug (bloqueada pelas rules).
        try {
          const published = await fetchPublishedBlogPosts();
          if (cancelled) return;
          const fromList = published.find((p) => p.slug === id || p.id === id);
          if (fromList) {
            setPost(fromList);
            return;
          }
        } catch (listErr) {
          console.warn('Lista de posts indisponível, tentando get direto:', listErr?.message || listErr);
        }

        // 2) Redirect de slug antigo (público)
        try {
          const redirectSnap = await getDoc(doc(db, 'blogRedirects', id));
          if (cancelled) return;
          if (redirectSnap.exists()) {
            const { newSlug } = redirectSnap.data();
            if (newSlug && newSlug !== id) {
              navigate(`/blog/${newSlug}`, { replace: true });
              return;
            }
          }
        } catch (redirectErr) {
          console.warn('blogRedirects indisponível:', redirectErr?.message || redirectErr);
        }

        // 3) Fallback: documento por id (post publicado)
        try {
          const docSnap = await getDoc(doc(db, 'blogPosts', id));
          if (cancelled) return;
          if (docSnap.exists()) {
            const data = { id: docSnap.id, ...docSnap.data() };
            if (data.status === 'published') {
              setPost(data);
              return;
            }
          }
        } catch (getErr) {
          console.warn('getDoc blogPosts falhou:', getErr?.message || getErr);
        }

        if (!cancelled) setPost(null);
      } catch (error) {
        console.error('Erro ao carregar post:', error);
        if (!cancelled) setPost(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPost();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  // Sidebar: categorias + imóveis — diferidos (cache / query enxuta)
  useEffect(() => {
    if (loading || !post) return undefined;

    let cancelled = false;
    let idleId;
    let timeoutId;

    const loadSidebar = async () => {
      try {
        const [publishedPosts, featured] = await Promise.all([
          fetchPublishedBlogPosts(),
          fetchHomeFeaturedProperties(8),
        ]);
        if (cancelled) return;

        setCategories(extractBlogCategoryCounts(publishedPosts));
        setFeaturedProperties(
          featured
            .map((data) => {
              const firstImage = data.images?.[0] || data.imageUrl || null;
              if (!firstImage) return null;
              return {
                id: data.id,
                ...data,
                imageUrl: firstImage,
                title: data.title || 'Imóvel sem título',
                price: data.price?.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }),
                rent: data.rent?.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })
              };
            })
            .filter(Boolean)
        );
      } catch (error) {
        console.error('Erro ao carregar sidebar do blog:', error);
      }
    };

    const run = () => {
      loadSidebar();
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(run, 300);
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [loading, post]);

  useEffect(() => {
    if (post && !hasViewed) {
      handlePostView(post);
    }
  }, [post, hasViewed, handlePostView]);

  const handlePreLoadingComplete = useCallback(() => {
    setIsPreLoading(false);
  }, []);

  useEffect(() => {
    if (!loading) {
      setIsPreLoading(false);
    }
  }, [loading]);

  // Controlar animações de entrada baseadas no scroll
  useEffect(() => {
    // Só inicia as animações após o pre-loading terminar
    if (!isPreLoading && post) {
      setIsLoaded(true);
      
      // Configuração do Intersection Observer
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute('data-animate');
            if (elementId) {
              setVisibleElements(prev => ({ ...prev, [elementId]: true }));
              observer.unobserve(entry.target);
            }
          }
        });
      }, observerOptions);

      // Observar elementos após um pequeno delay
      const timer = setTimeout(() => {
        const elementsToObserve = [
          headerRef.current,
          contentRef.current,
          sidebarRef.current
        ].filter(Boolean);

        elementsToObserve.forEach(element => {
          if (element) {
            observer.observe(element);
          }
        });
      }, 200);

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }
  }, [isPreLoading, post]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const blogSeo = useMemo(() => {
    if (!post) return null;
    return resolveBlogSeoMeta(post, companySettings, officialBaseUrl, id);
  }, [post, companySettings, officialBaseUrl, id]);

  const blogSupplementarySchemas = useMemo(() => {
    if (!post) return [];
    const schemas = [];
    if (post.schemaBreadcrumb !== false) {
      schemas.push(buildBlogPostBreadcrumbSchema(post, officialBaseUrl));
    }
    const faq = buildBlogFaqSchema(post);
    if (faq) schemas.push(faq);
    return schemas;
  }, [post, officialBaseUrl]);

  const blogMainSchema = useMemo(() => {
    if (!post || !blogSeo) return null;
    return buildBlogPostingJsonLd({
      post,
      settings: companySettings,
      baseUrl: officialBaseUrl,
      canonicalUrl: blogSeo.canonicalUrl,
      metaTitle: blogSeo.metaTitle,
      metaDescription: blogSeo.metaDescription,
      ogImage: blogSeo.ogImage,
    });
  }, [post, companySettings, officialBaseUrl, blogSeo]);

  const articleTags = useMemo(() => {
    if (!post?.tags) return [];
    return String(post.tags).split(/[,;]/).map((t) => t.trim()).filter(Boolean);
  }, [post?.tags]);

  if (!loading && !post) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        <SEO title="Post não encontrado" robots="noindex,nofollow" />
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">
            Post não encontrado
          </h1>
          <Link
            to="/blog"
            className="text-blue-500 hover:text-blue-600"
          >
            ← Voltar para o blog
          </Link>
        </div>
      </div>
    );
  }

  // Meta tags e JSON-LD via react-helmet-async (<SEO />)
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <PreLoader
        isLoading={isPreLoading}
        onLoadingComplete={handlePreLoadingComplete}
      />

      {loading ? (
        <BlogPostSkeleton />
      ) : (
        <>
      {post && blogSeo && (
        <SEO
          title={blogSeo.metaTitle}
          description={blogSeo.metaDescription}
          keywords={blogSeo.metaKeywords}
          ogTitle={blogSeo.ogTitle}
          ogDescription={blogSeo.ogDescription}
          ogImage={blogSeo.ogImage}
          canonicalUrl={blogSeo.canonicalUrl}
          pageType="article"
          robots={blogSeo.robots}
          twitterCard={blogSeo.twitterCard}
          twitterTitle={blogSeo.twitterTitle}
          twitterDescription={blogSeo.twitterDescription}
          twitterImage={blogSeo.twitterImage}
          twitterImageAlt={blogSeo.twitterImageAlt}
          schemaData={blogMainSchema}
          supplementarySchemas={blogSupplementarySchemas}
          includeBlogRss
          includeSiteSchemas
          articleMeta={{
            publishedTime: toSchemaIsoDate(post.createdAt),
            modifiedTime: toSchemaIsoDate(post.updatedAt || post.createdAt),
            section: post.category,
            tags: articleTags,
          }}
        />
      )}

      {/* Header do Post */}
      <div 
        ref={headerRef}
        data-animate="header"
        className={`bg-white shadow-sm transition-all duration-1000 ${visibleElements.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="max-w-7xl mx-auto px-4 pt-6 pb-12">
          {/* Breadcrumbs */}
          <div className="mb-8">
            <BreadcrumbNav
              items={buildBlogPostBreadcrumbs(post)}
              baseUrl={officialBaseUrl}
              skipSchema
            />
          </div>

          {/* Título e Data */}
          <h1 className="text-4xl font-montserrat font-semibold text-gray-900 mt-2 mb-4">
            {post.title}
          </h1>
          <div className="flex items-center text-sm text-gray-500">
            <i className="flaticon-calendario mr-2"></i>
            {formatDate(post.createdAt)}
          </div>
        </div>
      </div>

      {/* Conteúdo com Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Coluna Principal */}
          <div 
            ref={contentRef}
            data-animate="content"
            className={`lg:col-span-2 transition-all duration-1000 delay-200 ${visibleElements.content ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <article className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Imagem Principal */}
              {post.imageUrl && (
                <div className="w-full">
                  <LazyLoadImage
                    src={post.imageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    effect="blur"
                  />
                </div>
              )}

              {/* Conteúdo */}
              <div className="px-8 py-8 border-b border-gray-100">
                <div className="prose max-w-none formatted-content">
                  {typeof post.content === 'string' && post.content.includes('<') ? (
                    <div
                      className="text-gray-600 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                  ) : typeof post.content === 'string' ? (
                    post.content.split('\n').map((paragraph, index) => (
                      paragraph.trim() && (
                        <p key={index} className="text-gray-600 leading-relaxed mb-4 last:mb-0">
                          {paragraph}
                        </p>
                      )
                    ))
                  ) : (
                    <p className="text-gray-600 leading-relaxed">{post.content}</p>
                  )}
                </div>
              </div>

              {/* Imagens Adicionais */}
              {(post.image2Url || post.image3Url) && (
                <div className="px-8 py-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {post.image2Url && (
                      <div className="aspect-[16/9] w-full overflow-hidden rounded-lg">
                        <LazyLoadImage
                          src={post.image2Url}
                          alt={`${post.title} - Imagem adicional 1`}
                          className="w-full h-full object-cover object-center"
                          effect="blur"
                          wrapperProps={{
                            className: "w-full h-full"
                          }}
                        />
                      </div>
                    )}
                    {post.image3Url && (
                      <div className="aspect-[16/9] w-full overflow-hidden rounded-lg">
                        <LazyLoadImage
                          src={post.image3Url}
                          alt={`${post.title} - Imagem adicional 2`}
                          className="w-full h-full object-cover object-center"
                          effect="blur"
                          wrapperProps={{
                            className: "w-full h-full"
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Conclusão */}
              {post.conclusion && (
                <div className="px-8 py-8">
                  <h2 className="text-2xl font-semibold mb-4">Conclusão</h2>
                  <div className="prose max-w-none">
                    {typeof post.conclusion === 'string' ? (
                      post.conclusion.split('\n').map((paragraph, index) => (
                        paragraph.trim() && (
                          <p key={index} className="text-gray-600 leading-relaxed mb-4 last:mb-0">
                            {paragraph}
                          </p>
                        )
                      ))
                    ) : (
                      <p className="text-gray-600 leading-relaxed">{post.conclusion}</p>
                    )}
                  </div>
                </div>
              )}
            </article>
          </div>

          {/* Sidebar */}
          <div 
            ref={sidebarRef}
            data-animate="sidebar"
            className={`space-y-8 transition-all duration-1000 delay-400 ${visibleElements.sidebar ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {/* Mais Posts */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">Mais posts</h2>
              <div className="space-y-4">
                {categories.map(category => (
                  <div key={category.name} className="flex items-center justify-between">
                    <Link
                      to={`/blog?category=${category.name}`}
                      className="text-gray-600 hover:text-blue-500 transition-colors"
                    >
                      {category.name}
                    </Link>
                    <span className="text-gray-400">({category.count})</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Propriedades em Destaque */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold mb-4">Imóveis em Destaque</h2>
              <div className="grid grid-cols-1 gap-4">
                {featuredProperties.map((property, index) => (
                  property.imageUrl && (
                    <Link
                      key={property.id}
                      to={buildPropertyPath(property)}
                      className={`group block overflow-hidden rounded-lg bg-white hover:shadow-md transition-all duration-500 transform ${
                        visibleElements.sidebar 
                          ? 'opacity-100 translate-y-0 scale-100' 
                          : 'opacity-0 translate-y-8 scale-95'
                      }`}
                      style={{
                        transitionDelay: visibleElements.sidebar ? `${index * 100}ms` : '0ms'
                      }}
                    >
                      <div className="aspect-[16/9] w-full overflow-hidden">
                        <LazyLoadImage
                          src={property.imageUrl}
                          alt={property.title}
                          className="card-cover-img"
                          effect="blur"
                          wrapperProps={{
                            className: "w-full h-full"
                          }}
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-medium text-gray-900 group-hover:text-blue-500 transition-colors line-clamp-2">
                          {property.title}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1.5">
                          {property.neighborhood}, {property.city}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-blue-500 font-semibold">
                            {property.purpose === 'rent' 
                              ? `${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(property.rent)}/mês` 
                              : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(property.price)
                            }
                          </p>
                        </div>
                      </div>
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ScrollToTop />
        </>
      )}
    </div>
  );
}

export default BlogPost; 